import React, { useContext, useMemo, useState } from 'react';
import type {
  ControlProps,
  DropdownIndicatorProps,
  MenuPlacement,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { components } from 'react-select';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import SVGIcon from '../../components/SVGIcon';
import Select from '../../components/Select';
import type { SelectOptionType } from '../../components/SelectOption';
import SelectOptionContent from '../../components/SelectOptionContent';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostLinkAccess } from '../../graphql/generated/graphql';
import alertIcon from '../../icons/non-prefilled/16x16/alert.svg';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import publicIcon from '../../icons/non-prefilled/16x16/public.svg';
import removeIcon from '../../icons/non-prefilled/16x16/remove.svg';
import teamIcon from '../../icons/non-prefilled/16x16/team.svg';
import UpgradeBadge from '../../organization/UpgradeBadge';
import { isFeatureEnabled } from '../../utils/features';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import { openPricingModal } from '../../utils/url/pricingPath';
import usePublicContentPermissions from '../../utils/usePublicContentPermissions';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import * as styles from './SharingDropdownLink.module.scss';
import { useUpdatePostLinkAccess } from './postPermissionOperations';

const SHARING_DROPDOWN_LINK_POST_FRAGMENT = gql(`
  fragment SharingDropdownLink_post on Post {
    id
    linkAccess
    publishedAt
    shareSecret
    ...usePostPolicyCheck_post
    ...useUpdatePostLinkAccess_post
    ...postPath_post
  }
`);

type SharingDropdownLinkOption = SelectOptionType<PostLinkAccess> & {
  isInvalid?: boolean;
  badge?: React.ReactNode;
  onClick?: () => void;
};

const DropdownIndicator = <
  OptionType = unknown,
  IsMulti extends boolean = false,
>(
  props: DropdownIndicatorProps<OptionType, IsMulti>,
) => {
  return (
    <div className={styles.dropdownIndicator}>
      <Caret direction={props.selectProps.menuIsOpen ? 'up' : 'down'} />
    </div>
  );
};

const SetAnchorRefContext = React.createContext<
  (element: HTMLDivElement) => void
>(() => null);

const Control = <
  OptionType extends SharingDropdownLinkOption,
  IsMulti extends boolean = false,
>(
  props: ControlProps<OptionType, IsMulti>,
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorRef = useMemo(() => ({ current: anchorEl }), [anchorEl]);

  const value = props.getValue().at(0);

  return (
    <Tooltip
      content={
        value?.isInvalid && !props.selectProps.menuIsOpen ? value.tooltip : null
      }
      placement="bottom"
      danger
      anchorRef={anchorRef}
    >
      <SetAnchorRefContext.Provider value={setAnchorEl}>
        <components.Control {...props} className={styles.selectContainer} />
      </SetAnchorRefContext.Provider>
    </Tooltip>
  );
};

const SingleValue = <
  OptionType extends SharingDropdownLinkOption,
  IsMulti extends boolean = false,
>(
  props: SingleValueProps<OptionType, IsMulti>,
) => {
  const setAnchorRef = useContext(SetAnchorRefContext);
  return (
    <components.SingleValue {...props}>
      <div className={styles.accessContainer}>
        <div className={styles.secondary}>Link access:</div>
        <div className={styles.selectValue}>
          {props.data.label}
          {props.data.isInvalid && (
            <SVGIcon className={styles.alertIcon} ref={setAnchorRef} size={14}>
              {alertIcon}
            </SVGIcon>
          )}
        </div>
      </div>
    </components.SingleValue>
  );
};

const Option = (props: OptionProps<SharingDropdownLinkOption, false>) => (
  <div onClick={props.data.onClick}>
    <components.Option {...props}>
      <SelectOptionContent
        {...props.data}
        label={
          <>
            {props.data.label}
            {!!props.data.badge && (
              <span className={styles.selectOptionBadge}>
                {props.data.badge}
              </span>
            )}
          </>
        }
      />
    </components.Option>
  </div>
);

interface SharingDropdownLinkProps {
  post: DocumentType<typeof SHARING_DROPDOWN_LINK_POST_FRAGMENT>;
  onPricingModalOpen?: () => void;
}

const SharingDropdownLink = ({
  post,
  post: { linkAccess, publishedAt, shareSecret },
  onPricingModalOpen,
}: SharingDropdownLinkProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);
  const { isPermittedToPublicizePost } = usePublicContentPermissions();
  const updateLinkAccess = useUpdatePostLinkAccess();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [isCopied, setIsCopied] = React.useState(false);
  const handleCopy = async () => {
    const isPublic =
      linkAccess === PostLinkAccess.Public ||
      linkAccess === PostLinkAccess.PublicEdit;
    const shouldUseShareSecret = publishedAt
      ? isPublic
      : linkAccess !== PostLinkAccess.Disabled;
    await navigator.clipboard.writeText(
      fullOrgUrl(
        postPath(post, {
          shareSecret:
            shareSecret && shouldUseShareSecret ? shareSecret : undefined,
        }),
      ),
    );
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  const isDisabled = !canEdit;

  const options: SharingDropdownLinkOption[] = [
    ...(publishedAt
      ? []
      : [
          {
            value: PostLinkAccess.Disabled,
            icon: removeIcon,
            label: 'Disabled',
            helpText: 'Only the users above can access this post.',
            isDisabled,
          },
        ]),
    {
      value: PostLinkAccess.Internal,
      icon: publishedAt ? teamIcon : linkIcon,
      label: publishedAt ? 'Internal' : 'Direct',
      helpText: publishedAt
        ? 'Users and members above can access this post.'
        : 'Any teammate with this link can access this post.',
      isDisabled,
    },
    {
      value: PostLinkAccess.Public,
      icon: publicIcon,
      label: 'Public',
      helpText: 'Anyone on the internet with this link can access this post.',
      tooltipPlacement: 'bottom',
      isDisabled,
      ...(isPermittedToPublicizePost
        ? {}
        : {
            isDisabled: true,
            tooltip: 'Only admins can\nmake posts public',
          }),
      ...(isFeatureEnabled('public_posts_topics')
        ? {}
        : {
            badge: <UpgradeBadge />,
            isDisabled: true,
            isInvalid: true,
            tooltip: 'Public posts\nare not available\non your plan',
            onClick: () => {
              openPricingModal(dispatch, 'advancedTopics');
              onPricingModalOpen?.();
            },
          }),
    },
  ];

  const presentedLinkAccess =
    linkAccess === PostLinkAccess.PublicEdit
      ? PostLinkAccess.Public
      : linkAccess;

  return (
    <div className={styles.container}>
      {isCopied ? (
        <Button
          icon={
            <SVGIcon className={styles.check} size="small">
              {checkIcon}
            </SVGIcon>
          }
          variant="muted"
          size="small"
          disabled
        >
          Copied!
        </Button>
      ) : (
        <Button
          icon={linkIcon}
          onClick={handleCopy}
          variant="link"
          size="small"
        >
          Copy link
        </Button>
      )}
      <Select
        menuPlacement={(isMobile ? 'top' : 'auto') as MenuPlacement}
        onChange={(value) =>
          value !== presentedLinkAccess ? updateLinkAccess(post, value) : null
        }
        className={styles.select}
        isSearchable={false}
        options={options}
        value={presentedLinkAccess}
        components={{
          Control,
          SingleValue,
          Option,
          DropdownIndicator: canEdit ? DropdownIndicator : null,
        }}
      />
    </div>
  );
};

export default SharingDropdownLink;
